/*
 * Copyright (c) 2024.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/*
* api.service.ts
* 24-6-2024 - Jelmer Jellema - Spin in het Web B.V.
*/

import {Injectable} from "@angular/core";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {hash, restSuccess, startupcomponent} from "../types";
import {lastValueFrom} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {WPAIBase} from "../app/wpaibase";
import {SihwConfirmService} from "./sihwconfirm/sihwconfirm.service";

declare var wpai_apisettings: {
  nonce?: string,
  root: string,
  ns: string, //de namespace
  startupcomponent: startupcomponent,
  startupdata: any //eventuele data bij de shortcode bijvoorbeeld
}; //gezet door onze wp-plugin
@Injectable({
  providedIn: 'root'
})
export class ApiService extends WPAIBase {
  private readonly headers: HttpHeaders = new HttpHeaders({});
  private parsedStartupdata: hash<any>; //de startupdata geparsed
  constructor(private http: HttpClient, private toastr: ToastrService, private confirmService: SihwConfirmService) {
    super(true);
    this.log(`wpai_apisettings`, wpai_apisettings);
    if (!wpai_apisettings) {
      throw ('Niet goed gekoppeld aan WordPress');
    }

    if (wpai_apisettings.nonce) {
      //stuur hem altijd mee
      this.headers = this.headers.append('X-WP-Nonce', wpai_apisettings.nonce);
    }

    this.parsedStartupdata = {}; //standaard
    if (wpai_apisettings.startupdata)
    {
      try {
        this.parsedStartupdata = JSON.parse(wpai_apisettings.startupdata);
      }
      catch {
      }
    }
  }

  get startupcomponent(): startupcomponent {
    if (! wpai_apisettings)
    {
      throw ('Niet goed gekoppeld aan WordPress');
    }
    return wpai_apisettings.startupcomponent;
  }

  get startupdata(): any {
    return this.parsedStartupdata;
  }

  /**
   * Interne sender. Zie hieronder voor public implementaties
   * @param actie
   * @param params
   * @param errortoast
   * @private
   */
  private async send(actie: string, params: hash<any> = {}, errortoast: boolean = true): Promise<restSuccess> {
    this.log(`SEND: ${wpai_apisettings.root}${wpai_apisettings.ns}/${actie}`, params);
    try {
      let res = await lastValueFrom(this.http.post<restSuccess>(
        `${wpai_apisettings.root}${wpai_apisettings.ns}/${actie}`,
        params,
        {
          headers: this.headers
        }
      ));
      this.log(`RESULTAAT:`, res);
      if ((! res.success) && errortoast)
      {
        this.toast(res.melding ?? "Er is een fout opgetreden", "error");
        //maar wel door
      }
      return res;
    } catch (e: any) {
      this.logwarn(e);
      let melding = "Er is een fout opgetreden: " + (e.statusText ?? e.message ?? "onbekende fout");
      if (errortoast)
      {
        this.toast(melding, "error");
      }
      return <restSuccess>{
        success: false,
        melding: melding
      }
    }
  }

  //helpers voor meerdere of 1
  async arrayFromAction(actie: string, params: hash<any> = {}, errortoast: boolean = true): Promise<hash<any>[] | null> {
    let res = await this.send(actie, params, errortoast);
    if (res.success)
    {
      return <hash<any>[]> res.data;
    }
    else {
      return null;
    }
  }

  async hashFromAction(actie: string, params: hash<any> = {}, errortoast: boolean = true): Promise<hash<any> | null> {
   let res = await this.send(actie, params, errortoast);
   if (res.success) {
     return <hash<any>> res.data;
   }
   else {
     return null;
   }
  }

  async action(actie: string, params: hash<any> ={},errortoast: boolean = true): Promise<restSuccess> {
    return <restSuccess> await this.send(actie,params, errortoast);
  }

  /**
   * Toon een toast
   * @param message
   * @param type
   * @param title
   */
  public toast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info', title: string = '') {
    void this.toastr[type](message, title);
  }

  /**
   * Confirmdialoog
   * @param titel
   * @param vraag
   * @param jabtnTekst
   * @param neebtnTekst
   */
  public async  confirm(titel: string, vraag: string, jabtnTekst: string = "Ja", neebtnTekst: string = "Nee") {
    return this.confirmService.confirm(titel, vraag, jabtnTekst, neebtnTekst);
  }

}
